import { useEffect, useSyncExternalStore } from 'react';
import { getRepairs, createRepair, updateRepairStatus, updateRepairNotes, addRepairMedia, deleteRepairMedia, updateRepair, deleteRepair, hasConfirmedDiagnosisPayment } from '@/services/repairs';
import type { Repair, RepairStatus, RepairMediaUploadInput } from '@/types/repair';
import { useToast } from '@/contexts/ToastContext';
import { errMessage } from '@/utils/errors';
import { useTaxSettings } from '@/hooks/useTaxSettings';
import { useWirelessSettings } from '@/hooks/useWirelessSettings';
import { ensureTicketInvoice } from '@/services/wireless/autoInvoice';
import { supabase } from '@/services/supabase';
import { useAuth } from '@/hooks/useAuth';

interface Store {
  repairs: Repair[];
  loading: boolean;
  error: string | null;
}

let store: Store = { repairs: [], loading: true, error: null };
let activeAccessKey: string | null = null;
let loadedAccessKey: string | null = null;
let requestGeneration = 0;
const listeners = new Set<() => void>();

// A technician assigned to a job (or anyone else's ticket edit) previously
// only showed up for a client once it happened to refetch on its own —
// nothing pushed the change to an already-open tab. RLS already scopes what
// each row's postgres_changes events are delivered to (a technician only
// ever gets events for tickets/assignments they can SELECT), so this is
// safe to leave running for every role. Debounced because an assignment is
// itself a ticket_technicians insert plus often a tickets update landing
// within the same moment — one refetch covers both.
let reloadTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleReload() {
  if (reloadTimer) clearTimeout(reloadTimer);
  reloadTimer = setTimeout(() => { reloadTimer = null; reloadRepairs({ silent: true }); }, 300);
}

let realtimeStarted = false;
let fallbackPollTimer: ReturnType<typeof setInterval> | null = null;

function startFallbackPolling() {
  if (fallbackPollTimer) return;
  // Realtime may be unavailable because of a browser policy, proxy, or
  // temporary network failure. Ticket updates are important, so keep the
  // shared store fresh without allowing a WebSocket failure to crash React.
  fallbackPollTimer = setInterval(() => {
    reloadRepairs({ silent: true }).catch(error => {
      console.warn('[repairs] fallback refresh failed', error);
    });
  }, 45_000);
}

function startRealtimeSync() {
  if (realtimeStarted) return;
  realtimeStarted = true;

  // The production nginx policy currently permits HTTPS API calls but not
  // the WSS endpoint. WebKit treats that policy rejection as a fatal thrown
  // exception (and can unmount React), while Chromium merely logs it. Avoid
  // constructing the socket on that host and retain near-realtime behavior
  // through the safe polling fallback below.
  if (typeof window !== 'undefined' && window.location.hostname === 'operations.wirelesscares.com') {
    startFallbackPolling();
    return;
  }

  try {
    supabase
      .channel('wireless-tickets-sync')
      .on('postgres_changes', { event: '*', schema: 'wireless', table: 'tickets' }, scheduleReload)
      .on('postgres_changes', { event: '*', schema: 'wireless', table: 'ticket_technicians' }, scheduleReload)
      .subscribe(status => {
        if (status === 'SUBSCRIBED') {
          if (fallbackPollTimer) clearInterval(fallbackPollTimer);
          fallbackPollTimer = null;
          return;
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          startFallbackPolling();
        }
      });
  } catch (error) {
    // WebKit throws synchronously when CSP blocks the WebSocket. Chromium
    // only logs the same violation. Never let either behavior unmount the
    // entire React tree; polling keeps the app usable until Realtime returns.
    console.warn('[repairs] realtime unavailable; using polling', error);
    startFallbackPolling();
  }
}

function setStore(next: Partial<Store>) {
  store = { ...store, ...next };
  listeners.forEach(l => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return store;
}

// Shared across every screen that reads tickets (admin Tickets board, Tech
// Portal, Sales, Invoices, Reception panels) instead of each mounting its
// own useState copy — otherwise assigning a technician on one screen only
// updated that screen's local state, leaving the technician's own already-
// mounted Tech Portal (or any other open screen) showing the old assignment
// list until a hard refresh. Mirrors the same fix applied to
// useWirelessSettings / useTechnicians.
//
// `silent` is for background refreshes (poll / realtime): no loading flag, so
// lists and open forms stay put, a transient failure keeps the data already
// on screen, and the store is only touched if the data actually changed.
export async function reloadRepairs(opts: { silent?: boolean } = {}) {
  const silent = !!opts.silent;
  const accessKey = activeAccessKey;
  if (!accessKey) {
    setStore({ repairs: [], loading: false, error: null });
    return;
  }
  const generation = ++requestGeneration;
  if (!silent) setStore({ loading: true, error: null });
  try {
    const repairs = await getRepairs();
    if (accessKey === activeAccessKey && generation === requestGeneration) {
      if (silent && !store.error && JSON.stringify(repairs) === JSON.stringify(store.repairs)) return;
      setStore({ repairs, loading: false, error: null });
    }
  } catch (error) {
    if (accessKey === activeAccessKey && generation === requestGeneration) {
      if (silent) {
        console.warn('[useRepairs] background refresh failed', error);
        return;
      }
      console.error('[useRepairs] ticket refresh failed', error);
      setStore({ repairs: [], loading: false, error: 'Tickets could not be loaded. Please check your connection and try again.' });
    }
  } finally {
    if (accessKey === activeAccessKey && generation === requestGeneration && store.loading) {
      setStore({ loading: false });
    }
  }
}

export function useRepairs() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  const { user } = useAuth();
  const { showToast } = useToast();
  const { taxEnabled, vatRate, nhilGetfundRate } = useTaxSettings();
  const { settings } = useWirelessSettings();

  const accessKey = user ? `${user.id}:${user.role}` : null;
  // Effects run after render, so mask an old user's snapshot synchronously
  // during the account/role transition as well as clearing it below.
  const accessMatches = activeAccessKey === accessKey;
  const repairs = accessMatches ? snapshot.repairs : [];
  const loading = accessMatches ? snapshot.loading : !!accessKey;
  const error = accessMatches ? snapshot.error : null;

  useEffect(() => {
    if (activeAccessKey !== accessKey) {
      activeAccessKey = accessKey;
      loadedAccessKey = null;
      requestGeneration += 1;
      setStore({ repairs: [], loading: !!accessKey, error: null });
    }
    if (accessKey && loadedAccessKey !== accessKey) {
      loadedAccessKey = accessKey;
      reloadRepairs();
      startRealtimeSync();
    }
  }, [accessKey]);

  const add = async (r: Omit<Repair, 'id'>) => {
    try {
      const created = await createRepair(r);
      setStore({ repairs: [created, ...store.repairs] });
      showToast('Repair job created');
      // Every ticket gets an invoice immediately at creation — one with a
      // paid diagnosis fee is invoiced for that; a straight repair (no
      // diagnosis fee at all) gets an unpaid invoice for the quoted cost —
      // not just ones that later reach Ready. Gives staff something to track
      // the ticket by from the moment it's created. Best-effort: a failure
      // here shouldn't undo ticket creation; staff can still create the
      // invoice manually from the ticket panel.
      if (hasConfirmedDiagnosisPayment(created) || created.jobType === 'straight_repair') {
        const depositPaid = (created.payments ?? []).reduce((sum, p) => sum + (p.status === 'paid' ? p.amount : 0), 0);
        ensureTicketInvoice(created, { depositPaid, taxEnabled, vatRate, nhilGetfundRate, warrantyDays: settings?.warranty_days })
          .catch((invoiceError) => {
            console.error('Auto-invoice failed for', created.id, invoiceError);
            showToast(errMessage(invoiceError, `Ticket created, but the invoice couldn't be generated. Create it manually from the ticket panel.`), 'error');
          });
      }
      return created;
    } catch (e) {
      showToast(errMessage(e, 'Failed to create repair job'), 'error');
      throw e;
    }
  };

  const setStatus = async (id: string, status: RepairStatus) => {
    try {
      await updateRepairStatus(id, status);
      const now = new Date().toISOString();
      setStore({ repairs: store.repairs.map(r => r.id !== id ? r : {
        ...r,
        status,
        ...(status === 'ready' && r.status !== 'ready' ? { readyAt: now } : {}),
        ...(status === 'completed' || status === 'diagnosis_only_closed' ? { completedDate: now } : {}),
      }) });
      showToast('Status updated');
    } catch (e) {
      showToast(errMessage(e, 'Failed to update status'), 'error');
      throw e;
    }
  };

  const setNotes = async (id: string, notes: string[]) => {
    try {
      await updateRepairNotes(id, notes);
      setStore({ repairs: store.repairs.map(r => r.id === id ? { ...r, notes } : r) });
    } catch (e) {
      showToast(errMessage(e, 'Failed to save note'), 'error');
      throw e;
    }
  };

  const updateStatus = async (id: string, status: RepairStatus) => setStatus(id, status);

  const addNote = async (id: string, note: string) => {
    const repair = store.repairs.find(r => r.id === id);
    if (!repair) return;
    await setNotes(id, [...(repair.notes ?? []), note]);
  };

  const addMedia = async (id: string, input: RepairMediaUploadInput) => {
    try {
      const media = await addRepairMedia(id, input);
      setStore({
        repairs: store.repairs.map((repair) => (
          repair.id === id
            ? { ...repair, media: [media, ...(repair.media ?? [])] }
            : repair
        )),
      });
      showToast('Media uploaded');
      return media;
    } catch (e) {
      showToast(errMessage(e, 'Failed to upload media'), 'error');
      throw e;
    }
  };

  const removeMedia = async (id: string, mediaId: string) => {
    try {
      await deleteRepairMedia(id, mediaId);
      setStore({
        repairs: store.repairs.map((repair) => (
          repair.id === id
            ? { ...repair, media: (repair.media ?? []).filter((item) => item.id !== mediaId) }
            : repair
        )),
      });
      showToast('Photo removed');
    } catch (e) {
      showToast(errMessage(e, 'Failed to remove photo'), 'error');
      throw e;
    }
  };

  const patchRepair = async (id: string, patch: Partial<Repair>) => {
    try {
      await updateRepair(id, patch);
      setStore({
        repairs: store.repairs.map((repair) => (
          repair.id === id ? { ...repair, ...patch } : repair
        )),
      });
      showToast('Repair updated');
    } catch (e) {
      showToast(errMessage(e, 'Failed to update repair'), 'error');
      throw e;
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteRepair(id);
      setStore({ repairs: store.repairs.filter(r => r.id !== id) });
      showToast('Repair deleted');
    } catch (e) {
      showToast(errMessage(e, 'Failed to delete repair'), 'error');
      throw e;
    }
  };

  return { repairs, loading, error, reload: () => reloadRepairs(), add, setStatus, setNotes, updateStatus, addNote, addMedia, removeMedia, patchRepair, remove };
}
